import { Arguments, Payment } from '@repo-level/core-front';

import { PAYMENTS_REDIRECT_ROUTES } from './constants';
import { sendDepositPaymentMethodsFailedLoading } from 'modules/googleAnalytics';
import { withRetry } from 'modules/common/helpers/withRetry';

export const fetchPaymentMethods = async (
  currency: string,
  paymentAction = Payment.Action.Deposit
) => {
  const isDeposit = paymentAction === Payment.Action.Deposit;
  const PaymentsAPI = window.PaymentsAPI;

  if (!PaymentsAPI) {
    if (isDeposit) {
      sendDepositPaymentMethodsFailedLoading({ error: 'no_payments_lib' });
    }
    return Promise.resolve({ data: [] });
  }

  try {
    // Clear PaymentsAPI cache to ensure fresh data
    await PaymentsAPI.resetCache();

    // Call PaymentsAPI.getMethods with retry logic
    const response = await withRetry({
      operation: () => PaymentsAPI.getMethods({ currency, paymentAction }),
      onRetryError: (err) => {
        sendDepositPaymentMethodsFailedLoading({
          error: err instanceof Error ? err.message : JSON.stringify(err),
        });
      },
    });

    // If it's a deposit and response is empty, report an error with the empty value
    if (isDeposit && !(response && response.length)) {
      sendDepositPaymentMethodsFailedLoading({
        error: 'empty_value',
        value: JSON.stringify(response),
      });
    }

    // Return fetched payment methods
    return { data: response };
  } catch (error) {
    // Log error and report it (for deposits)
    console.log('[PaymentsAPI.getMethods][withRetry] error', error);
    if (isDeposit) {
      sendDepositPaymentMethodsFailedLoading({
        error: error instanceof Error ? error.message : JSON.stringify(error),
      });
    }
    // Return empty data on failure
    return { data: [] };
  }
};

export const getMethodFields = (
  args: Omit<Arguments.GetMethodFieldsArgs, 'paymentAction'>,
  paymentAction = Payment.Action.Deposit
) => {
  return (
    window.PaymentsAPI?.getMethodFields({
      paymentAction,
      ...args,
    })
      .then((res) => ({ data: res }))
      .catch((error) => {
        console.error('Get method fields error', error);
        return { data: error };
      }) || Promise.reject({ data: undefined })
  );
};

export const deleteSavedPaymentMethod = (
  args: Omit<Arguments.DeleteSavedProfileArgs, 'paymentAction'>
) => {
  return (
    window.PaymentsAPI?.deleteSavedProfile({
      ...args,
      paymentAction: Payment.Action.Deposit,
    })
      .then(() => ({ data: undefined }))
      .catch((error) => {
        return { data: error };
      }) || Promise.resolve({ data: undefined })
  );
};

export const submitFormPayment = (
  args: Omit<Arguments.SubmitArgs, 'paymentAction'>,
  paymentAction = Payment.Action.Deposit
) => {
  return (
    window.PaymentsAPI?.submit({
      ...args,
      paymentAction,
    })
      .then((res) => ({ data: res }))
      .catch((error) => {
        return { data: error };
      }) || Promise.reject({ data: undefined })
  );
};

export const resetCachePayments = () => {
  return (
    window.PaymentsAPI?.resetCache().catch(() => {
      console.error('Payments reset cache error');
    }) || Promise.reject()
  );
};

const buildStatusUrl = (eventId?: string) => (path?: string) =>
  `${window.location.origin}${path}${eventId ? `?event_id=${eventId}` : ''}`;

export const configPayments = (language: string, eventId?: string) => {
  const statusUrl = buildStatusUrl(eventId);

  const successUrl = statusUrl(PAYMENTS_REDIRECT_ROUTES.SUCCESS);
  const failureUrl = statusUrl(PAYMENTS_REDIRECT_ROUTES.FAILURE);
  const pendingUrl = statusUrl(PAYMENTS_REDIRECT_ROUTES.PENDING);
  const supportUrl = statusUrl(PAYMENTS_REDIRECT_ROUTES.SUPPORT);

  return (
    window.PaymentsAPI?.config({
      language,
      redirectRoutes: {
        successUrl,
        failureUrl,
        pendingUrl,
        supportUrl,
      },
    }) || Promise.reject
  );
};
